import { useId } from "@reach/auto-id"
import { forwardRef } from "react"

import {
  FormControl,
  FormErrorMessage,
  FormMessage,
  Icon,
} from "@trueskin-web/components"
import { useFormControl } from "@trueskin-web/components/src/form-control"
import { arrowRightIcon } from "@trueskin-web/theme/icons/arrowRightIcon"
import { infoIcon } from "@trueskin-web/theme/icons/infoIcon"

import FormLabel from "./label"

const sizes = {
  xlg: {
    height: "56px",
    px: 4,
  },
  lg: {
    height: "48px",
    px: 4,
  },
  md: {
    height: "40px",
    px: 3,
  },
}

const Input = forwardRef((props, ref) => {
  const {
    as: Comp = "input",
    size = "md",
    isReadOnly,
    isFullWidth = true,
    hoverBorderColor,
    ...otherProps
  } = props
  const formControl = useFormControl(props)

  return (
    <Comp
      ref={ref}
      aria-invalid={formControl.isInvalid}
      readOnly={formControl.isReadOnly}
      aria-readonly={isReadOnly}
      disabled={formControl.isDisabled}
      aria-disabled={formControl.isDisabled}
      required={formControl.isRequired}
      aria-required={formControl.isRequired}
      sx={{
        display: "flex",
        alignItems: "center",
        width: isFullWidth ? "100%" : undefined,
        appearance: "none",
        fontFamily: "inherit",
        fontSize: 2,
        outline: 0,
        border: "1px",
        borderColor: "textLight",
        borderRadius: "8px",
        color: "inherit",
        lineHeight: 1.5,
        "&:hover": {
          borderColor: hoverBorderColor ? hoverBorderColor : "text",
        },
        "&:focus": {
          zIndex: 1,
          borderColor: hoverBorderColor ? hoverBorderColor : "text",
        },
        variant: "vwo.form.input",
        "&:disabled, &:disabled:focus, &:disabled:hover": {
          borderColor: "inherit",
          color: "disabled",
          cursor: "not-allowed",
        },
        "&[aria-invalid=true]": {
          borderColor: "attention",
        },
        "&::placeholder": {
          opacity: 0,
        },
        "&:focus, &:valid, &:disabled": {
          pt: 5,
        },
        "& + label": {
          position: "absolute",
          left: 4,
          display: "flex",
          lineHeight: "smallerHeading",
          color: "textDark",
          pointerEvents: "none",
          transition: ".2s ease",
        },
        "&:focus + label, &:disabled + label, &:not(:placeholder-shown) + label":
          {
            opacity: 1,
            top: 2,
            fontSize: 0,
            fontWeight: "bold",
            zIndex: 1,
          },
        "&:disabled ~ *": {
          color: "disabled",
        },
        ...sizes[size],
      }}
      {...otherProps}
    />
  )
})

Input.displayName = "Input"

const InputControl = forwardRef(
  (
    {
      className,
      input,
      meta,
      label,
      message,
      required,
      messagePlaceholder,
      mobileMessagePlaceholder,
      desktopMessagePlaceholder,
      errorMessagePlaceholder,
      mobileErrorMessagePlaceholder,
      desktopErrorMessagePlaceholder,
      ...props
    },
    ref
  ) => {
    const id = useId()

    return (
      <FormControl
        isInvalid={
          (meta.error || (meta.submitError && !meta.dirtySinceLastSubmit)) &&
          meta.touched
        }
        isRequired={required}
        className={className}
      >
        <div
          data-testid={`${props["data-testid"]}-wrapper`}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Input id={id} ref={ref} size="xlg" {...input} {...props} />

          {label && (
            <FormLabel
              htmlFor={id}
              sx={{
                "&::before": {
                  transition: "all .2s ease",
                  position: "absolute",
                  height: "50%",
                  width: "100%",
                  content: '""',
                },
                "&::after": {
                  transition: "all .2s ease",
                  position: "absolute",
                  height: "50%",
                  width: "100%",
                  top: "50%",
                  content: '""',
                },
              }}
            >
              {label}
            </FormLabel>
          )}

          {props.as === "select" ? (
            <Icon
              icon={arrowRightIcon}
              sx={{
                position: "absolute",
                right: 4,
                transform: "rotate(90deg)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          ) : null}
        </div>
        <FormErrorMessage
          mobilePlaceholder={
            errorMessagePlaceholder || mobileErrorMessagePlaceholder
          }
          desktopPlaceholder={
            errorMessagePlaceholder || desktopErrorMessagePlaceholder
          }
          sx={{
            "& > div": {
              display: "inline-flex",
              alignItems: "center",
            },
          }}
        >
          <Icon icon={infoIcon} color="attention" size="sm" sx={{ mr: 1 }} />
          {meta.error || meta.submitError}
        </FormErrorMessage>
        <FormMessage
          mobilePlaceholder={messagePlaceholder || mobileMessagePlaceholder}
          desktopPlaceholder={messagePlaceholder || desktopMessagePlaceholder}
          sx={{ color: "textDark" }}
        >
          {message}
        </FormMessage>
      </FormControl>
    )
  }
)

InputControl.displayName = "InputControl"

export { Input }

export default InputControl
